// Near Miss from the guest page, Stage 5 piece 6. Words: OBSERVATIONS_SCAN_WORDS.md rev 3, section 1
// (approved by Robert 2026-10-12). Mockup: MOCKUP_OBSERVATIONS_SCAN.html rev 2.
//
// Anyone who scans the label. No sign-in, name optional, asset from the scan.
// A sheet shaped like Report a Problem, with its own list (not mixed into Work Requests).
// The first four picks are High, the rest Medium. One line about what happened is required.
// It lands in observations/OBS-#### and emails the site's Alerts contacts and the program's
// Observation Alerts contacts. One per device every 5 minutes, checked on the server.
// The ESP Manager reviews it: Mark Reviewed, or Make A Work Request (linked both ways).
//
// Robert builds the full Near Miss later (follow-up contact, When, routing). This one extends.
//
// Pure: data in, data out. Unit tested.

import { programContactsFor, siteContactsFor, siteNameOf, type AlertWorld, type PlannedAlert } from "./alerts";
import type { EmailRequest, Recipient } from "./email";
import { startedTextOf } from "./work-started";
import type { Priority, RequestLine } from "./work-requests";

export const NEAR_MISS_PICKS = [
  "Shock Or Tingle",
  "Arc Or Flash",
  "Live When It Should Be Dead",
  "Lockout Missing Or Removed Early",
  "Door Or Cover Open On Live Equipment",
  "Work Without The Right PPE",
  "Other",
] as const;
export type NearMissPick = (typeof NEAR_MISS_PICKS)[number];

const HIGH_PICKS: readonly NearMissPick[] = ["Shock Or Tingle", "Arc Or Flash", "Live When It Should Be Dead", "Lockout Missing Or Removed Early"];

export const NEAR_MISS_NOTE_MAX = 300;
export const NEAR_MISS_NAME_MAX = 80;
export const NO_NAME = "No name given";
export const NEAR_MISS_REASON = "Near miss";
export const NEAR_MISS_TOO_SOON = "You just sent a near miss. Try again in a few minutes.";
/** The Sheet's Observations Log reached OBS-0005 (Registry 10, 2026-10-12). Firebase numbering carries on from there. */
export const OBS_FIRST_NUMBER = 6;
export const INJURY_LINE = "Someone hurt? Get help now and tell your supervisor. This form is for close calls where nobody was hurt.";
export const REVIEW_MAX = 300;

export function pickPriority(p: string): Priority {
  return (HIGH_PICKS as readonly string[]).includes(p) ? "High" : "Medium";
}

/** Only known picks, in the sheet's order. */
export function cleanPicks(picks: readonly unknown[]): NearMissPick[] {
  return NEAR_MISS_PICKS.filter((p) => picks.includes(p));
}

export function nearMissPriority(picks: readonly string[]): Priority {
  return picks.some((p) => pickPriority(p) === "High") ? "High" : "Medium";
}

export interface NearMissInput {
  picks: string[];
  note: string;
  name: string;
}

export function nearMissProblem(i: NearMissInput): string | null {
  if (!cleanPicks(i.picks).length) return "Pick what almost happened.";
  if (!i.note.trim()) return "Write one line about what happened.";
  return null;
}

// ---- The record: companies/{c}/observations/OBS-#### ----

export type NearMissStatus = "Open" | "Reviewed";

export interface NearMissRow {
  observationId: string;
  /** "Near Miss" today. The old Sheet observations come over in piece 7 as "Observation". */
  kind: string;
  /** "guest" from the label. */
  source: string;
  siteNumber: string;
  assetId: string;
  location: string;
  equipment: string;
  picks: string[];
  note: string;
  name: string;
  priority: Priority;
  status: NearMissStatus;
  reviewNote: string;
  reviewedByName: string;
  reviewedAt: string | null;
  workRequestId: string;
  createdOn: string;
  createdAt: string;
}

export interface NewNearMissInput {
  observationId: string;
  siteNumber: string;
  assetId: string;
  location: string;
  equipment: string;
  input: NearMissInput;
  /** Stamped for the abuse guard. Never shown on a screen. */
  deviceKey: string;
  today: string;
  nowIso: string;
}

export function newNearMissOf(i: NewNearMissInput): NearMissRow & { deviceKey: string } {
  const picks = cleanPicks(i.input.picks);
  return {
    observationId: i.observationId,
    kind: "Near Miss",
    source: "guest",
    siteNumber: i.siteNumber,
    assetId: i.assetId,
    location: i.location,
    equipment: i.equipment,
    picks,
    note: i.input.note.trim().slice(0, NEAR_MISS_NOTE_MAX),
    name: i.input.name.trim().slice(0, NEAR_MISS_NAME_MAX) || NO_NAME,
    priority: nearMissPriority(picks),
    status: "Open",
    reviewNote: "",
    reviewedByName: "",
    reviewedAt: null,
    workRequestId: "",
    createdOn: i.today,
    createdAt: i.nowIso,
    deviceKey: i.deviceKey,
  };
}

const s = (v: unknown) => (typeof v === "string" ? v : "");

export function nearMissRowOf(id: string, d: Record<string, unknown>): NearMissRow {
  const picks = Array.isArray(d.picks) ? d.picks.filter((p): p is string => typeof p === "string") : [];
  return {
    observationId: id,
    kind: s(d.kind) || "Near Miss",
    source: s(d.source) || "guest",
    siteNumber: s(d.siteNumber),
    assetId: s(d.assetId),
    location: s(d.location),
    equipment: s(d.equipment),
    picks,
    note: s(d.note),
    name: s(d.name) || NO_NAME,
    priority: d.priority === "High" || d.priority === "Medium" || d.priority === "Low" ? d.priority : nearMissPriority(picks),
    status: d.status === "Reviewed" ? "Reviewed" : "Open",
    reviewNote: s(d.reviewNote),
    reviewedByName: s(d.reviewedByName),
    reviewedAt: s(d.reviewedAt) || null,
    workRequestId: s(d.workRequestId),
    createdOn: s(d.createdOn),
    createdAt: s(d.createdAt),
  };
}

/** "Shock or tingle, Arc or flash" in lists and email. */
export function picksText(picks: readonly string[]): string {
  return picks.map((p) => p.charAt(0) + p.slice(1).toLowerCase()).join(", ");
}

/** The work request's lines: one per pick, with the pick's priority. */
export function workRequestLinesOf(r: NearMissRow): RequestLine[] {
  return r.picks.map((p) => ({ finding: `Near miss: ${p.charAt(0) + p.slice(1).toLowerCase()}`, priority: pickPriority(p) }));
}

// ---- Review ----

export type ReviewAction = "reviewed" | "workRequest";

export function reviewProblem(r: NearMissRow, action: unknown, note: string): string | null {
  if (action !== "reviewed" && action !== "workRequest") return "Mark Reviewed or Make A Work Request?";
  if (r.status === "Reviewed") return `${r.observationId} is already reviewed.`;
  if (action === "reviewed" && !note.trim()) return "Write one line: what was done?";
  return null;
}

// ---- The list ----

/** Site first, then Open or All. Open first, High at the top, newest first. */
export function nearMissRowsFor(all: readonly NearMissRow[], site: string, openOnly: boolean): NearMissRow[] {
  return all
    .filter((r) => (site === "all" || r.siteNumber === site) && (!openOnly || r.status === "Open"))
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === "Open" ? -1 : 1;
      if (a.priority !== b.priority) return a.priority === "High" ? -1 : b.priority === "High" ? 1 : 0;
      return b.createdAt.localeCompare(a.createdAt);
    });
}

/** Admin Home tile: the amber count is every Open near miss. */
export function nearMissTileOf(all: readonly NearMissRow[]): { line: string; alert: number } {
  const open = all.filter((r) => r.status === "Open");
  const high = open.filter((r) => r.priority === "High").length;
  if (!all.length) return { line: "None yet", alert: 0 };
  if (!open.length) return { line: `${all.length} reviewed`, alert: 0 };
  return { line: [`${open.length} open`, high ? `${high} High` : ""].filter(Boolean).join(" · "), alert: open.length };
}

// ---- The email ----

const dedupe = (groups: Recipient[][]): Recipient[][] => {
  const seen = new Set<string>();
  return groups.map((g) =>
    g.filter((r) => {
      const k = r.email.trim().toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }),
  );
};

/** One email to the site's Alerts contacts and one to the program's Observation Alerts contacts. Null when nobody. */
export function nearMissAlert(r: NearMissRow, world: AlertWorld): PlannedAlert | null {
  const site = siteNameOf(world, r.siteNumber);
  const sign = world.companyName ? `Sent by SafeTag for the ${world.companyName} Electrical Safety Program.` : "Sent by SafeTag for the Electrical Safety Program.";
  const [siteTo, programTo] = dedupe([siteContactsFor(world, r.siteNumber, "alerts"), programContactsFor(world, "observationAlerts")]);
  const base = {
    siteNumber: r.siteNumber,
    siteName: site,
    companyName: world.companyName,
    subject: `Near Miss · ${r.equipment || "Equipment"} · ${site} · ${r.priority}`,
    headline: "Someone reported a near miss at the equipment.",
    lines: [`Reported from the SafeTag label, no sign-in. Nobody was hurt. ${r.priority === "High" ? "Look at it today." : "Look at it this week."}`],
    rows: [
      { label: "What almost happened", value: picksText(r.picks) },
      { label: "What happened", value: r.note },
      { label: "Priority", value: r.priority },
      { label: "Reported by", value: r.name || NO_NAME },
      { label: "Equipment", value: r.equipment || "Not on record" },
      { label: "Location", value: r.location || "Not on record" },
      { label: "Asset ID", value: r.assetId },
      { label: "Reported", value: startedTextOf(r.createdAt) },
      { label: "Reference", value: r.observationId },
    ],
    button: { label: "Open The Near Miss", path: `#/near-miss/r/${encodeURIComponent(r.observationId)}` },
    reason: NEAR_MISS_REASON,
    recordId: r.observationId,
  };
  const emails: EmailRequest[] = [];
  if (siteTo!.length) emails.push({ ...base, to: siteTo!, toLabel: `${site} alerts`, footer: `You get this because you are an Alerts contact at ${site}. ${sign}` });
  if (programTo!.length) emails.push({ ...base, to: programTo!, toLabel: "Observation alerts", footer: `You get this because you are an Observation Alerts contact for the program. ${sign}` });
  return emails.length ? { key: `near-miss-${r.observationId}`, emails } : null;
}

// ---- The sheet on the guest page ----

export interface NearMissSheet {
  picks: string[];
  note: string;
  name: string;
  busy: boolean;
  error: string;
  /** The reference once sent. */
  doneId: string;
}

export const EMPTY_NEAR_MISS: NearMissSheet = { picks: [], note: "", name: "", busy: false, error: "", doneId: "" };

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** True when the sheet can send: a pick and a line. */
export function nearMissReady(s: NearMissSheet): boolean {
  return nearMissProblem(s) === null && !s.busy;
}

export function nearMissSheetHtml(s: NearMissSheet, assetLine: string): string {
  const head = `<div class="g-overlay show" data-g="close"></div>
    <div class="g-bottom-sheet open" role="dialog" aria-label="Near Miss">
      <div class="g-sheet-handle"></div>`;
  if (s.doneId)
    return `${head}<div class="g-report-success show">
        <div class="g-success-icon gr-done">✓</div><div class="g-success-title gr-done-t">Near Miss Sent</div>
        <div class="g-success-sub">Thank you. Near misses keep the next person safe. Reference ${esc(s.doneId)}.</div>
        <button class="g-success-close" type="button" data-g="close">Done</button></div></div>`;
  const chips = NEAR_MISS_PICKS.map((p) => {
    const on = s.picks.includes(p);
    const tone = pickPriority(p) === "High" ? "high" : "med";
    return `<button class="g-priority-pill gr-pick${on ? ` ${tone} active` : ""}" type="button" data-nmpick="${esc(p)}" aria-pressed="${on}">${esc(p)}</button>`;
  }).join("");
  return `${head}
      <div class="g-sheet-header"><span class="g-sheet-title">Near Miss</span><button class="g-sheet-close" type="button" data-g="close">✕</button></div>
      <div class="g-sheet-asset-ref">${esc(assetLine)}</div>
      <div class="g-form-section"><div class="gr-warn">${esc(INJURY_LINE)}</div></div>
      <div class="g-form-section"><div class="g-form-label">What almost happened? <span>Pick one or more</span></div><div class="g-priority-row gr-picks">${chips}</div></div>
      <div class="g-form-section"><div class="g-form-label">What happened? <span>Required</span></div>
        <textarea class="g-obs-textarea" id="nm-note" rows="2" maxlength="${NEAR_MISS_NOTE_MAX}" placeholder="One line about what happened">${esc(s.note)}</textarea></div>
      <div class="g-form-section"><div class="g-form-label">Your name <span>Optional</span></div>
        <input class="g-name-input" id="nm-name" maxlength="${NEAR_MISS_NAME_MAX}" autocomplete="name" placeholder="Leave blank to send without a name" value="${esc(s.name)}"></div>
      ${s.error ? `<div class="g-form-section"><div class="gr-err" role="status">${esc(s.error)}</div></div>` : ""}
      <div class="g-form-section"><button class="g-sheet-submit" type="button" data-g="send-nearmiss"${nearMissReady(s) ? "" : " disabled"}>${s.busy ? "Sending…" : "Send Near Miss"}</button></div>
    </div>`;
}
